import { useMemo, useState } from 'react'
import { Download, FileSpreadsheet, Loader2, ScanSearch } from 'lucide-react'

import { useAppStore } from '@core/store'
import { downloadBlob } from '@core/lib/downloadBlob'
import { escreverXlsx } from '@core/lib/folhas'
import { inventariar, inventarioParaFolhas, type Inventario } from '@viewer/lib/inventario/inventarioPsets'
import { modeloDePreenchimento, type CabecalhoDePropriedade } from '@modules/ifc-edit/lib/importarFolha'
import { depoisDePintar, modeloEmTexto } from './modeloEmTexto'
import { AVISO, BTN, BTN_PRIMARIO, INPUT, NOTA } from './estilos'

const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const MAX_LINHAS = 400

function hoje(): string {
  const d = new Date()
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** Inventário do modelo activo, guardado por modelo enquanto o painel
 *  estiver aberto noutro separador. */
const inventarios = new Map<string, Inventario>()

/** A coluna do modelo de preenchimento para uma linha do inventário. A
 *  forma (lista, intervalo, quantidade) e o tipo só vão para o cabeçalho
 *  quando o modelo usa um só: assim a importação de volta não os tem de
 *  adivinhar. */
function colunaDe(l: Inventario['linhas'][number]): CabecalhoDePropriedade {
  const base = { pset: l.conjunto, propriedade: l.propriedade }
  if (l.genero === 'Qto') {
    return /^IfcQuantity[A-Za-z]+$/.test(l.classeDaPropriedade)
      ? { ...base, forma: 'quantidade', classeDaQuantidade: l.classeDaPropriedade }
      : base
  }
  const forma =
    l.classeDaPropriedade === 'IfcPropertyListValue'
      ? ('lista' as const)
      : l.classeDaPropriedade === 'IfcPropertyBoundedValue'
        ? ('intervalo' as const)
        : undefined
  return {
    ...base,
    ...(forma ? { forma } : {}),
    ...(l.tiposDeValor.length === 1 ? { tipoDeValor: l.tiposDeValor[0] } : {}),
  }
}

export default function SeparadorInventario({ modelId, ficheiro }: { modelId: string; ficheiro: string }) {
  const [inv, setInv] = useState<Inventario | null>(() => inventarios.get(modelId) ?? null)
  const [aLer, setALer] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [filtro, setFiltro] = useState('')
  const [escolhidas, setEscolhidas] = useState<Set<string>>(new Set())
  const [comMaterial, setComMaterial] = useState(false)
  const [sistemas, setSistemas] = useState<Set<string>>(new Set())
  const [prevModel, setPrevModel] = useState(modelId)
  if (prevModel !== modelId) {
    setPrevModel(modelId)
    setInv(inventarios.get(modelId) ?? null)
    setEscolhidas(new Set())
    setComMaterial(false)
    setSistemas(new Set())
  }

  const ler = async () => {
    setALer(true)
    setErro(null)
    await depoisDePintar()
    try {
      const m = modeloEmTexto(modelId)
      if (!m) throw new Error('Este modelo não tem o texto do IFC em memória (abriu-se de um .frag?). Abre o .ifc.')
      const novo = inventariar(m)
      inventarios.set(modelId, novo)
      setInv(novo)
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
    } finally {
      setALer(false)
    }
  }

  const linhas = useMemo(() => {
    if (!inv) return []
    const f = filtro.trim().toLowerCase()
    if (!f) return inv.linhas
    return inv.linhas.filter((l) =>
      `${l.classe} ${l.conjunto} ${l.propriedade} ${l.tiposDeValor.join(' ')} ${l.unidade}`.toLowerCase().includes(f),
    )
  }, [inv, filtro])

  const chave = (l: Inventario['linhas'][number]) => `${l.classe}\0${l.origem}\0${l.conjunto}\0${l.propriedade}`

  const descarregarInventario = () => {
    if (!inv) return
    const base = ficheiro.replace(/\.ifc$/i, '')
    downloadBlob(escreverXlsx(inventarioParaFolhas(inv, ficheiro, hoje())), `${base}_inventario.xlsx`, XLSX)
    useAppStore.getState().pushToast(`${base}_inventario.xlsx descarregado.`, { type: 'success' })
  }

  const descarregarModelo = () => {
    if (!inv) return
    const m = modeloEmTexto(modelId)
    if (!m) return
    const sel = inv.linhas.filter((l) => escolhidas.has(chave(l)))
    const classes = [...new Set(sel.map((l) => l.classe))]
    const vistas = new Set<string>()
    const colunas = sel
      .filter((l) => {
        const k = `${l.conjunto}\0${l.propriedade}`
        if (vistas.has(k)) return false
        vistas.add(k)
        return true
      })
      .map(colunaDe)
    const folha = modeloDePreenchimento(m, { classes, colunas, material: comMaterial, sistemas: [...sistemas] })
    const base = ficheiro.replace(/\.ifc$/i, '')
    downloadBlob(escreverXlsx([folha]), `${base}_preencher.xlsx`, XLSX)
    const total = colunas.length + (comMaterial ? 1 : 0) + sistemas.size
    useAppStore.getState().pushToast(
      `Modelo de preenchimento com ${folha.linhas.length - 1} elemento(s) e ${total} coluna(s).`,
      { type: 'success' },
    )
  }

  const selInstancia =
    (inv ? inv.linhas.filter((l) => escolhidas.has(chave(l))).length : 0) + (comMaterial ? 1 : 0) + sistemas.size

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 p-3 text-[11px]">
      <p className={NOTA}>
        Que classes têm que Psets, com que propriedades, tipo de valor e unidade, e quantos elementos os preenchem.
        Lido do texto do IFC, tal como está no ficheiro (sem as alterações pendentes).
      </p>
      <div className="flex flex-wrap items-center gap-1">
        <button type="button" className={BTN_PRIMARIO} onClick={() => void ler()} disabled={aLer}>
          {aLer ? <Loader2 size={12} className="animate-spin" /> : <ScanSearch size={12} />}
          {inv ? 'Ler outra vez' : 'Ler o inventário'}
        </button>
        <button type="button" className={BTN} onClick={descarregarInventario} disabled={!inv}>
          <Download size={12} /> Excel
        </button>
        <button
          type="button"
          className={BTN}
          onClick={descarregarModelo}
          disabled={selInstancia === 0}
          title="Uma folha com os elementos das classes escolhidas e uma coluna por propriedade escolhida, com os valores actuais. Preenche e volta a importar no separador Importar."
        >
          <FileSpreadsheet size={12} /> Modelo de preenchimento{selInstancia > 0 ? ` (${selInstancia})` : ''}
        </button>
      </div>
      {erro && <div className={AVISO}>{erro}</div>}
      {inv && (
        <>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-[var(--color-text-muted)]">
            <span>Esquema {inv.esquema ?? 'não declarado'}</span>
            <span>{inv.linhas.length} propriedades distintas</span>
            <span>{inv.totalDeElementosComDados} elementos com dados</span>
            <span title="As unidades que o projecto declara, com a entidade de origem">
              Unidades do projecto:{' '}
              {inv.unidadesDoProjecto.length === 0
                ? 'nenhuma declarada'
                : inv.unidadesDoProjecto.map((u) => `${u.simbolo} ${u.origem}`).join(', ')}
            </span>
          </div>
          {inv.unidadesAmbiguas.length > 0 && (
            <div className={AVISO}>
              O projecto declara mais de uma unidade para{' '}
              {inv.unidadesAmbiguas.map((u) => `${u.tipo} (${u.candidatas.join(', ')})`).join('; ')}. Não se escolhe
              nenhuma: as propriedades desse tipo ficam com unidade ambígua.
            </div>
          )}
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
            <details className="min-w-0 flex-1">
              <summary className="cursor-pointer text-[var(--color-text-muted)]">
                Materiais: {inv.materiais.length} distinto(s) em {inv.elementosComMaterial} elemento(s)
              </summary>
              <label className="mt-1 flex items-center gap-1" title="Uma coluna Material no modelo de preenchimento, com o material actual de cada elemento">
                <input type="checkbox" checked={comMaterial} onChange={(e) => setComMaterial(e.target.checked)} />
                Incluir a coluna Material no modelo de preenchimento
              </label>
              <ul className="mt-1 max-h-28 overflow-auto pl-3">
                {inv.materiais.map((x) => (
                  <li key={`${x.simples}${x.texto}`} className={x.simples ? '' : 'text-[var(--color-text-muted)]'}>
                    {x.texto} <span className="font-mono">({x.elementos})</span>
                    {!x.simples && ': composto, não se muda por folha'}
                  </li>
                ))}
              </ul>
            </details>
            <details className="min-w-0 flex-1">
              <summary className="cursor-pointer text-[var(--color-text-muted)]">
                Classificações: {inv.sistemas.length} sistema(s), {inv.classificacoes.length} código(s)
              </summary>
              {inv.sistemas.map((sis) => (
                <label key={sis} className="mt-1 flex items-center gap-1" title={`Uma coluna "Classificação: ${sis}" no modelo de preenchimento`}>
                  <input
                    type="checkbox"
                    checked={sistemas.has(sis)}
                    onChange={(e) => {
                      const s = new Set(sistemas)
                      if (e.target.checked) s.add(sis)
                      else s.delete(sis)
                      setSistemas(s)
                    }}
                  />
                  Incluir a coluna <span className="font-mono">Classificação: {sis}</span>
                </label>
              ))}
              <p className="mt-1 text-[var(--color-text-muted)]">
                Para um sistema que o ficheiro ainda não tem, acrescenta à mão a coluna{' '}
                <span className="font-mono">Classificação: nome do sistema</span>.
              </p>
              <ul className="mt-1 max-h-28 overflow-auto pl-3">
                {inv.classificacoes.map((c) => (
                  <li key={`${c.sistema}\0${c.codigo}`}>
                    <span className="text-[var(--color-text-muted)]">{c.sistema}</span>{' '}
                    <span className="font-mono">{c.codigo || '(sem código)'}</span>
                    {c.nome && ` ${c.nome}`} <span className="font-mono">({c.elementos})</span>
                  </li>
                ))}
              </ul>
            </details>
          </div>
          <input className={INPUT} placeholder="Filtrar por classe, Pset, propriedade, tipo ou unidade" value={filtro} onChange={(e) => setFiltro(e.target.value)} />
          <div className="min-h-0 flex-1 overflow-auto rounded border border-[var(--color-border)]">
            <table className="w-full border-collapse text-[10px]">
              <thead className="sticky top-0 bg-[var(--color-panel)] text-left text-[var(--color-text-muted)]">
                <tr>
                  <th className="px-1 py-1" title="Escolher para o modelo de preenchimento" />
                  <th className="px-1 py-1">Classe</th>
                  <th className="px-1 py-1">Conjunto</th>
                  <th className="px-1 py-1">Propriedade</th>
                  <th className="px-1 py-1">Tipo</th>
                  <th className="px-1 py-1">Unidade</th>
                  <th className="px-1 py-1 text-right" title="Com valor / elementos que têm a propriedade">Preenchido</th>
                  <th className="px-1 py-1">Exemplos</th>
                </tr>
              </thead>
              <tbody>
                {linhas.slice(0, MAX_LINHAS).map((l) => {
                  const k = chave(l)
                  return (
                    <tr key={k} className="border-t border-[var(--color-border)]/50 hover:bg-[var(--color-bg)]">
                      <td className="px-1">
                        <input
                          type="checkbox"
                          title={l.origem === 'tipo' ? 'Incluir no modelo de preenchimento: uma linha por tipo, e o valor é herdado pelas instâncias' : 'Incluir no modelo de preenchimento'}
                          checked={escolhidas.has(k)}
                          onChange={(e) => {
                            const s = new Set(escolhidas)
                            if (e.target.checked) s.add(k)
                            else s.delete(k)
                            setEscolhidas(s)
                          }}
                        />
                      </td>
                      <td className="px-1 font-mono">
                        {l.classe}
                        {l.origem === 'tipo' && (
                          <span
                            className="ml-1 rounded bg-[var(--color-accent-bg)] px-1 text-[9px] text-[var(--color-accent)]"
                            title={`Pset do tipo, herdado por ${l.instanciasAbrangidas ?? 0} instância(s)`}
                          >
                            tipo
                          </span>
                        )}
                      </td>
                      <td className="px-1">{l.conjunto}</td>
                      <td className="px-1">{l.propriedade}</td>
                      <td className="px-1 font-mono" title={l.classeDaPropriedade}>{l.tiposDeValor.join(', ') || l.classeDaPropriedade}</td>
                      <td className="px-1" title={l.explicacaoDaUnidade}>{l.unidade}</td>
                      <td className={`px-1 text-right font-mono ${l.comValor < l.elementos ? 'text-[var(--color-warn-text)]' : ''}`}>
                        {l.comValor}/{l.elementos}
                      </td>
                      <td className="max-w-[160px] truncate px-1 text-[var(--color-text-muted)]" title={l.exemplos.join(' | ')}>
                        {l.exemplos.join(' | ')}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {linhas.length > MAX_LINHAS && (
            <p className={NOTA}>
              A mostrar {MAX_LINHAS} de {linhas.length}. Filtra, ou descarrega o Excel para ver todas.
            </p>
          )}
        </>
      )}
    </div>
  )
}
